// BoardViewCompact.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Hooks
import useBoardGroups from 'views/board/hooks/useBoardGroups';
import useDefaultNodeOpen from 'views/hooks/useDefaultNodeOpen';

// CSS
import './BoardViewCompact.css';

// Icons
import Icon from 'svg/Icon';

// Components
import BoardViewCompactButton from './button/BoardViewCompactButton';

const MAX_GROUPS = 4;
const MAX_ITEMS_PER_GROUP = 3;

/**
 * BoardViewCompact
 *
 * Compact read-only board summary for constrained surfaces.
 * Shows only populated groups as vertical sections.
 * 
 * @param {object} view - Board view configuration.
 * @param {object} typeDef - Type definition for the board's nodes.
 * @param {array} nodes - Array of nodes to display in the board.
 * @param {string} language - Current language for localization.
 * 
 * @returns {ReactNode} - BoardViewCompact component.
 */
const BoardViewCompact = React.memo(({ view, typeDef, nodes = [], language = 'en' }) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- HANDLERS ----- //

  const handleOpenNode = useDefaultNodeOpen();

  // ----- HOOKS ----- //

  // Get board groups and columns based on the provided nodes and view configuration

  const {
    boardData,
  } = useBoardGroups({
    view,
    typeDef,
    nodes,
    language,
  });

  // ----- MEMOS ----- //

  /**
   * Compute visible groups for the compact view, filtering out empty groups and limiting to MAX_GROUPS.
   * For each group, prepare a preview of items to display and count of remaining items.
   */

  const visibleGroups = useMemo(() => (
    boardData.columns
      .filter((column) => (boardData.groups.get(column.key) || []).length > 0)
      .slice(0, MAX_GROUPS)
      .map((column) => {
        const items = boardData.groups.get(column.key) || [];
        const previewItems = items.slice(0, MAX_ITEMS_PER_GROUP);
        const remainingCount = Math.max(0, items.length - previewItems.length);
        return {
          ...column,
          items,
          previewItems,
          remainingCount,
        };
      })
  ), [boardData]);

  // ----- RENDER ----- //

  return (
    <div className="board-view-compact-container">

      {visibleGroups.map((group) => (

        <section
          className="board-view-compact-group"
          key={`board_compact_${group.key}`}
        >

          {/* GROUP HEADER */}
          <header
            className="board-view-compact-group-header"
          >

            {/* GROUP HEADER DOT */}
            <span
              className="board-view-compact-group-dot"
              style={{
                backgroundColor: group.color || theme.backgroundMuted
              }}
            />

            {/* GROUP ICON */}
            {group.iconKey ? (

              <span className="board-view-compact-group-icon" aria-hidden="true">
                <Icon
                  iconKey={group.iconKey}
                  width={14}
                  height={14}
                  color={theme.foregroundMuted}
                />
              </span>
            ) : null}

            {/* GROUP TITLE */}
            <span
              className="board-view-compact-group-title"
            >
              {group.label}
            </span>

          </header>

          {/* GROUP ITEM LIST */}
          <div className="board-view-compact-list">

            {group.previewItems.map((node) => (

              <BoardViewCompactButton
                key={node?.key || `${group.key}_${node?.title || 'node'}`}
                node={node}
                onClick={() => handleOpenNode(node)}
              />

            ))}

          </div>

          {/* MORE BUTTON */}
          {group.remainingCount > 0 && (

            <div
              className="board-view-compact-more"
            >
              +{group.remainingCount} more
            </div>
          )}
        </section>

      ))}

    </div>
  );
});

// Dev Tools
BoardViewCompact.displayName = 'BoardViewCompact';

export default BoardViewCompact;
